// AsmBoard — combined ASM approvals command center (Jun 16, 2026).
//
// One board for everything the ASM (Alex) approves:
//   • Agreements    — install-agreement FIRST approval gate (pending_approval).
//                     Moved from Chad → ASM Jun 16, 2026. Chad/super keep it as
//                     backup. Mounts the shared AgreementReviewModal.
//   • Canvas        — canvasser-submitted appointment queue (embeds the shared
//                     CanvasApprovalsInner from /canvas/approvals).
//   • Drone Intake  — ASM Gate 1 (pending_asm_review) + Gate 2 (report_approved
//                     → convert to lead). Embeds the shared AlexASMView.
//   • SPIFFs        — last week's SPIFFs, approved Monday (Oct 27, 2026); approving
//                     makes their Books bills. SpiffApprovalsPanel.
//
// Route: /asm (asm + admin + sales_director; super-users bypass). A matching
// home summary card (AsmApprovalsHomeCard) links here with live counts.

import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib/supabase";
import { useTabParam } from "../lib/useTabParam";
import type { Deal } from "../lib/types";
import DealLink from "../components/DealLink";
import { daysInStage, daysInPhase, daysSinceCreated } from "../lib/dealAge";
import { AgreementReviewModal } from "../components/deal/ScopeSection";
import AlexASMView from "../components/drone/AlexASMView";
import { CanvasApprovalsInner } from "./CanvasApprovals";
import { CallListInner } from "./CallList";
import SpiffApprovalsPanel from "../components/crew/SpiffApprovalsPanel";
import { fetchSpiffsWaitingCount } from "../components/crew/crewQueries";

type TabKey = "agreements" | "canvas" | "drone" | "calllist" | "spiffs";

interface Counts {
  agreements: number;
  canvas: number;
  drone: number;
  /** Re-canvass leads with no RM yet — Greg's "flag the ASM" ask (Aug 20, 2026). */
  calllist: number;
  /** SPIFFs waiting for approval (Oct 27, 2026). */
  spiffs: number;
}

export default function AsmBoard() {
  const navigate = useNavigate();
  const [tab, setTab] = useTabParam<TabKey>("tab", "agreements", ["agreements", "canvas", "drone", "calllist", "spiffs"]);
  const [counts, setCounts] = useState<Counts>({ agreements: 0, canvas: 0, drone: 0, calllist: 0, spiffs: 0 });

  const refreshCounts = useCallback(async () => {
    const [ag, cv, dr, cl, sp] = await Promise.all([
      supabase.from("deals").select("id", { count: "exact", head: true }).eq("agreement_status", "pending_approval"),
      supabase.from("pending_canvas_appointments").select("id", { count: "exact", head: true }).eq("status", "pending"),
      supabase.from("deals").select("id", { count: "exact", head: true }).in("stage", ["pending_asm_review", "report_approved"]),
      supabase.from("recanvass_leads").select("id", { count: "exact", head: true }).eq("status", "new"),
      fetchSpiffsWaitingCount().catch(() => 0),
    ]);
    setCounts({ agreements: ag.count ?? 0, canvas: cv.count ?? 0, drone: dr.count ?? 0, calllist: cl.count ?? 0, spiffs: sp });
  }, []);

  // Refresh badge counts on mount and whenever the tab changes — keeps the
  // Canvas/Drone badges reasonably fresh after actions taken inside those
  // embedded panels (which don't report back through refreshCounts).
  useEffect(() => { void refreshCounts(); }, [refreshCounts, tab]);

  const tabs: Array<{ key: TabKey; label: string; count: number }> = [
    { key: "agreements", label: "Agreements", count: counts.agreements },
    { key: "canvas", label: "Canvas", count: counts.canvas },
    { key: "drone", label: "Drone Intake", count: counts.drone },
    { key: "calllist", label: "Call List", count: counts.calllist },
    { key: "spiffs", label: "SPIFFs", count: counts.spiffs },
  ];

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white px-4 py-3">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/")} className="rounded-md px-2 py-1 text-sm font-semibold text-slate-700 hover:bg-slate-100">← Home</button>
            <div>
              <div className="text-base font-bold text-slate-900">ASM Approvals</div>
              <div className="text-xs text-slate-500">Agreements, canvasser appointments, drone intake and the weekly SPIFFs — one queue.</div>
            </div>
          </div>
          <span className="text-2xl">🦆</span>
        </div>
      </div>

      <div className={`mx-auto p-4 ${tab === "calllist" ? "max-w-[1500px]" : "max-w-5xl"}`}>
        {/* Tab pills with count badges */}
        <div className="mb-4 flex flex-wrap gap-2">
          {tabs.map((t) => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold transition ${
                  active ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50 border border-slate-200"
                }`}
              >
                {t.label}
                {t.count > 0 && (
                  <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                    active ? "bg-white/20 text-white" : "bg-orange-100 text-orange-700"
                  }`}>
                    {t.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {tab === "agreements" && <AgreementApprovalsPanel onChanged={refreshCounts} />}
        {tab === "canvas" && <CanvasApprovalsInner />}
        {tab === "drone" && <AlexASMView />}
        {tab === "calllist" && <CallListInner embedded />}
        {tab === "spiffs" && <SpiffApprovalsPanel onChanged={refreshCounts} />}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Agreement approvals panel — pending_approval queue + AgreementReviewModal.
// Mirrors the ChadReview ApprovalsTab pattern (lean list → load full deal on
// click → mount modal). Only the FIRST gate (pending_approval) is shown here;
// the second gate (ready_for_dispatch / Release to Robert) stays on Chad's
// Ops Review board.
// ─────────────────────────────────────────────────────────────────────────────

type AgreementRow = Pick<
  Deal,
  | "id"
  | "homeowner_name"
  | "property_street"
  | "property_city"
  | "property_state"
  | "claim_number"
  | "insurance_company"
  | "owner_id"
  | "days_in_stage"
  | "stage_entered_at"
  | "phase_entered_at"
  | "created_at"
  | "agreement_revision_count"
  | "scope_gate"
>;

// scope_gate (Oct 30, 2026): the RM's override request when the scope check
// blocked the submit — the card says so before Alex opens it.
const AG_COLS =
  "id,homeowner_name,property_street,property_city,property_state,claim_number,insurance_company,owner_id,days_in_stage,stage_entered_at,phase_entered_at,created_at,agreement_revision_count,scope_gate";

function AgreementApprovalsPanel({ onChanged }: { onChanged: () => void }) {
  const { member, role, isSuperUser } = useAuth();
  const [rows, setRows] = useState<AgreementRow[] | null>(null);
  const [members, setMembers] = useState<Record<string, string>>({});
  const [err, setErr] = useState<string | null>(null);

  const [reviewDealId, setReviewDealId] = useState<string | null>(null);
  const [reviewDeal, setReviewDeal] = useState<Deal | null>(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const load = useCallback(async () => {
    try {
      const [agRes, memRes] = await Promise.all([
        supabase
          .from("deals")
          .select(AG_COLS)
          .eq("agreement_status", "pending_approval")
          .order("stage_entered_at", { ascending: true }),
        supabase.from("team_roster").select("id,full_name").eq("is_active", true),
      ]);
      if (!mountedRef.current) return;
      const error = agRes.error ?? memRes.error;
      if (error) { setErr(error.message); return; }
      setRows((agRes.data ?? []) as AgreementRow[]);
      const map: Record<string, string> = {};
      (memRes.data ?? []).forEach((m) => { map[m.id] = m.full_name; });
      setMembers(map);
    } catch (e) {
      if (mountedRef.current) setErr((e as Error)?.message ?? "Failed to load agreements.");
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // Hydrate the full Deal when a card is reviewed (the modal needs scope numbers,
  // signing_link, items_not_performing_lines, etc. the lean Pick doesn't carry).
  useEffect(() => {
    if (!reviewDealId) { setReviewDeal(null); setReviewError(null); return; }
    let cancelled = false;
    setReviewLoading(true);
    setReviewError(null);
    (async () => {
      try {
        const { data, error } = await supabase.from("deals").select("*").eq("id", reviewDealId).single();
        if (cancelled || !mountedRef.current) return;
        if (error) { setReviewError(error.message); setReviewDeal(null); }
        else setReviewDeal(data as Deal);
      } catch (e) {
        if (cancelled || !mountedRef.current) return;
        setReviewError((e as Error)?.message ?? "Failed to load deal.");
        setReviewDeal(null);
      } finally {
        if (!cancelled && mountedRef.current) setReviewLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [reviewDealId]);

  function closeReview() {
    setReviewDealId(null);
    setReviewDeal(null);
    setReviewError(null);
  }

  if (err) return <div className="rounded-lg bg-red-50 p-3 text-sm text-red-800">⚠ {err}</div>;
  if (!rows) return <div className="text-sm text-slate-500">Loading agreements…</div>;

  return (
    <>
      {rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-12 text-center">
          <div className="text-sm font-medium text-slate-700">No agreements waiting for approval.</div>
          <div className="mt-1 text-xs text-slate-500">When an RM submits an install agreement, it shows up here for review.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((d) => {
            const ownerName = d.owner_id ? members[d.owner_id] : null;
            const addr = [d.property_street, d.property_city, d.property_state].filter(Boolean).join(", ");
            const waiting = daysInStage(d);
            return (
              <div key={d.id} className="rounded-lg border border-orange-200 bg-white shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3 p-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-base font-semibold text-slate-900">
                        <DealLink id={d.id}>{d.homeowner_name ?? "(no name)"}</DealLink>
                      </span>
                      <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-indigo-700">
                        Pending approval
                      </span>
                      {(d.agreement_revision_count ?? 0) > 0 && (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                          rev {d.agreement_revision_count}
                        </span>
                      )}
                      {d.scope_gate?.request && (
                        <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-rose-700" title="The deal doesn't match the carrier's scope; the RM asked for an override.">
                          Needs an override
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 text-xs text-slate-600">{addr || "—"}</div>
                    {d.scope_gate?.request?.note && (
                      <div className="mt-1 max-w-xl text-xs text-rose-800">
                        RM's note: “{d.scope_gate.request.note.length > 160 ? `${d.scope_gate.request.note.slice(0, 157)}…` : d.scope_gate.request.note}”
                      </div>
                    )}
                    <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                      {ownerName && <span>RM: {ownerName}</span>}
                      {d.insurance_company && <span>{d.insurance_company}</span>}
                      {d.claim_number && <span>#{d.claim_number}</span>}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className="text-xs text-slate-400">{waiting}d waiting · {daysInPhase(d) ?? "?"}d in phase · {daysSinceCreated(d) ?? "?"}d since created</span>
                    <button
                      onClick={() => setReviewDealId(d.id)}
                      className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                    >
                      Review & approve →
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Loading shim while the full Deal hydrates */}
      {reviewDealId && reviewLoading && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm">
          <div className="rounded-xl bg-white px-5 py-4 text-sm text-slate-700 shadow-xl">Loading agreement…</div>
        </div>
      )}
      {reviewDealId && reviewError && !reviewLoading && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm">
          <div className="max-w-sm rounded-xl bg-white px-5 py-4 shadow-xl">
            <div className="mb-1 text-sm font-semibold text-rose-700">Couldn't load deal</div>
            <div className="mb-3 text-xs text-slate-600">{reviewError}</div>
            <button onClick={closeReview} className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-medium text-white">Close</button>
          </div>
        </div>
      )}

      {reviewDeal && (
        <AgreementReviewModal
          deal={reviewDeal}
          open={true}
          role={role}
          isSuperUser={isSuperUser}
          member={member}
          onClose={closeReview}
          onReload={async () => {
            await load();
            await onChanged();
            const { data } = await supabase.from("deals").select("*").eq("id", reviewDeal.id).single();
            if (data && mountedRef.current) setReviewDeal(data as Deal);
          }}
        />
      )}
    </>
  );
}
